import { type JSX, useRef } from 'react';

import { Grid } from '@change/ds-layout';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { ResponsiveBox, SectionSeparator, Separator } from 'src/app/pages/petitionDetails/shared/components';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from 'src/app/pages/petitionDetails/shared/waypoints';
import { MembershipAwareness, MembershipAwarenessSunrise } from 'src/app/shared/components/membershipAwareness';

import { PetitionStarter as PetitionStarterSunrise } from '../components/PetitionStarterSunrise';
import { useIsFullSunriseEnabled } from '../hooks/isFullSunriseEnabled';
import { useStickyBehavior } from '../hooks/mainContainer/useStickyBehavior';

import { ContentSummary as ContentSummarySunrise } from './contentSummarySunrise';
import { DecisionMakerSection } from './DecisionMakerSection';
import { DescriptionSunriseContainer } from './descriptionSunrise';
import { HeadingContainerMobile } from './HeadingMobile';
import { MediaHitsContainerSunrise } from './MediaHitsContainerSunrise';
import { MobileActionButtonContainer } from './mobileActionButton';
import { MobileActionButtonContainer as MobileActionButtonContainerSunrise } from './mobileActionButtonSunrise';
import { PetitionTimestamp } from './PetitionTimestamp';
import {
	RecentSignersViewportNormal as RecentSignersViewportNormalSunrise,
	RecentSignersViewportSmall as RecentSignersViewportSmallSunrise,
} from './recentSignersSunrise';
import { ReportAbuseContainer } from './ReportAbuse';
import { ReportAbuseContainerSunrise as ReportAbuseSunriseContainer } from './ReportAbuseSunrise';
import { SapCtaContainer } from './SapCta';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { SharePetition } from './share/SharePetition';
import { SharePetition as SharePetitonSunrise } from './shareSunriseExperiment/SharePetition';
import { SideContainer } from './side';
import { SideContainer as SideContainerSunrise } from './sideSunrise';
import { SignatureStatsMobileContainer } from './stats/signatureStats';
import { TagBreadcrumbs } from './TagBreadcrumbs';
import { TheSupportersSunrise } from './TheSupportersSunrise';
import { UpdatesContainer } from './Updates';
import { UpdatesContainer as UpdatesSunriseContainer } from './UpdatesSunrise';

/*
Using CSS grid for better keyboard/swipe navigation
╔═══╦═══╗
║ 1 ║   ║
╠═══╣ 2 ║
║ 3 ║   ║
╚═══╩═══╝
*/

// eslint-disable-next-line max-lines-per-function
export function MainContainer(): JSX.Element {
	const {
		data: {
			petition: { id: petitionId, slug },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { signed },
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const stickyStyles = useStickyBehavior(signatureBlockRef);

	const {
		data: {
			isSunriseShareModuleExperimentEnabled,
			isSunriseReportAbuseEnabled,
			isSunriseMembershipAwarenessEnabled,
			sunriseActionPanelExperimentVariation,
			isSunrisePetitionUpdatesEnabled,
		},
	} = useIsFullSunriseEnabled();

	const sunriseActionPanelVariants: Record<string, JSX.Element> = {
		control: <SideContainer containerRef={signatureBlockRef} />,
		fake_control: <SideContainer containerRef={signatureBlockRef} />,
		// For now desktop and mobile share variants, with desktop v1 and v2 being the exact same.
		v1: <SideContainerSunrise containerRef={signatureBlockRef} />,
		v2: <SideContainerSunrise containerRef={signatureBlockRef} />,
	};

	const sunriseMobileActionPanelVariants: Record<string, (sticky: boolean) => JSX.Element> = {
		control: (sticky: boolean) => <MobileActionButtonContainer sticky={sticky} />,
		fake_control: (sticky: boolean) => <MobileActionButtonContainer sticky={sticky} />,
		v1: (sticky: boolean) => <MobileActionButtonContainerSunrise sticky={sticky} variant="v1" />,
		v2: (sticky: boolean) => <MobileActionButtonContainerSunrise sticky={sticky} variant="v2" />,
	};

	return (
		<Grid className="px-0 lg:max-w-[1376px] lg:!grid-cols-[repeat(14,minmax(0,1fr))]">
			{/* Had to add an arbitrary media query to fix layout bug in the 767 pixel width */}
			<div className="col-span-full md:container lg:!col-start-2 lg:!col-end-11 [@media(min-width:768px)]:col-span-5">
				<HeadingContainerMobile />

				<div className="p-4 md:p-0">
					<ResponsiveBox viewportSize="normal">
						<RecentSignersViewportNormalSunrise />

						<ContentSummarySunrise />
						<DescriptionSunriseContainer />
						{isSunriseReportAbuseEnabled ? <ReportAbuseSunriseContainer /> : <ReportAbuseContainer />}
						<PetitionStarterSunrise />
					</ResponsiveBox>

					<ResponsiveBox viewportSize="small">
						{sunriseMobileActionPanelVariants[sunriseActionPanelExperimentVariation](false)}

						<AfterMobileActionButtonWaypoint />
						<SignatureStatsMobileContainer />
						<RecentSignersViewportSmallSunrise />

						<ContentSummarySunrise />
						<DescriptionSunriseContainer />
						{isSunriseReportAbuseEnabled ? <ReportAbuseSunriseContainer /> : <ReportAbuseContainer />}
						<PetitionStarterSunrise />
					</ResponsiveBox>

					<ScrollTrackingWaypointContainer name="scroll_past_body" />
					<AfterDescriptionWaypoint />
				</div>
			</div>
			{/* We need to modify the row-span to match the exact last row before the first full width module */}
			<div className="absolute mb-20 md:relative md:col-start-6 md:col-end-9 lg:col-start-11 lg:col-end-[15] lg:row-span-3">
				<ResponsiveBox viewportSize="small">
					{sunriseMobileActionPanelVariants[sunriseActionPanelExperimentVariation](true)}
				</ResponsiveBox>
				<ResponsiveBox viewportSize="normal" sx={{ ...stickyStyles }}>
					{sunriseActionPanelVariants[sunriseActionPanelExperimentVariation]}
				</ResponsiveBox>
			</div>

			<div className="container col-span-full lg:col-start-2 lg:col-end-11">
				<SapCtaContainer />

				<DecisionMakerSection />

				<TheSupportersSunrise />

				{!isSunriseMembershipAwarenessEnabled && (
					<div data-qa="target-div-robert" className="my-6">
						<MembershipAwareness
							sourceLocation="petition_details"
							petitionId={petitionId}
							slug={slug}
							signed={signed}
						/>
					</div>
				)}
			</div>

			{isSunriseMembershipAwarenessEnabled && (
				<div className="col-span-full my-6 px-4 md:px-0">
					<MembershipAwarenessSunrise
						sourceLocation="petition_details"
						petitionId={petitionId}
						slug={slug}
						signed={signed}
					/>
				</div>
			)}

			<div className="container col-span-full lg:col-start-2 lg:col-end-11">
				<ScrollTrackingWaypointContainer name="scroll_updates_section" />

				<SectionSeparator />
				{isSunrisePetitionUpdatesEnabled ? <UpdatesSunriseContainer /> : <UpdatesContainer />}
			</div>

			<div className="col-span-full mb-20 lg:col-start-1 lg:col-end-[15]">
				<MediaHitsContainerSunrise />
			</div>

			<div className="container col-span-full lg:col-start-2 lg:col-end-11">
				{isSunriseShareModuleExperimentEnabled ? <SharePetitonSunrise /> : <SharePetition />}

				<SectionSeparator />
				<PetitionTimestamp />
				<Separator />
				<TagBreadcrumbs />
			</div>
		</Grid>
	);
}
